import { useEffect } from 'react'
import {
  Modal,
  Form,
  Switch,
  Input,
  Checkbox,
  Select,
  message,
  Alert,
  Space,
  Popconfirm,
} from 'antd'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { hospitalsApi } from '@/api/hospitals'
import { auth0Api } from '@/api/auth0'
import type { SSOConfiguration } from '@/types/hospital'

interface SSOEditModalProps {
  hospitalId: string
  open: boolean
  onCancel: () => void
  existingConfig: SSOConfiguration | null
}

interface FormValues {
  enabled: boolean
  connection?: string
  group?: string
  permissions?: {
    bi?: boolean
    sender?: boolean
    readonly?: boolean
    receiver?: boolean
    dashboard?: boolean
    teamInsights?: boolean
  }
}

export const SSOEditModal = ({ hospitalId, open, onCancel, existingConfig }: SSOEditModalProps) => {
  const [form] = Form.useForm<FormValues>()
  const queryClient = useQueryClient()

  // Watch the enabled field to conditionally require connection
  const ssoEnabled = Form.useWatch('enabled', form)

  // Fetch available Auth0 connections
  const { data: connectionsData, isLoading: loadingConnections } = useQuery({
    queryKey: ['auth0-connections'],
    queryFn: async () => {
      const response = await auth0Api.getConnections()
      return response.data.data.connections
    },
    enabled: open,
  })

  // Update SSO config mutation
  const updateMutation = useMutation({
    mutationFn: async (values: SSOConfiguration) => {
      return hospitalsApi.updateSSOConfig(hospitalId, values)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['hospital-sso', hospitalId] })
      message.success('SSO configuration updated successfully')
      onCancel()
    },
    onError: (error: any) => {
      message.error(error.response?.data?.error?.message || 'Failed to update SSO configuration')
    },
  })

  // Delete SSO config mutation
  const deleteMutation = useMutation({
    mutationFn: async () => {
      return hospitalsApi.deleteSSOConfig(hospitalId)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['hospital-sso', hospitalId] })
      message.success('SSO configuration removed successfully')
      onCancel()
    },
    onError: (error: any) => {
      message.error(error.response?.data?.error?.message || 'Failed to remove SSO configuration')
    },
  })

  // Set initial form values when modal opens
  useEffect(() => {
    if (open) {
      if (existingConfig) {
        form.setFieldsValue({
          enabled: existingConfig.enabled,
          connection: existingConfig.connection,
          group: existingConfig.group,
          permissions: existingConfig.permissions || {},
        })
      } else {
        form.resetFields()
        form.setFieldsValue({
          enabled: true,
          permissions: {},
        })
      }
    }
  }, [open, existingConfig, form])

  const handleOk = () => {
    form
      .validateFields()
      .then((values) => {
        const config: SSOConfiguration = {
          enabled: values.enabled,
          connection: values.connection,
          group: values.group,
          permissions: values.permissions,
        }
        updateMutation.mutate(config)
      })
      .catch(() => {
        // Validation failed - form will show errors
        // No need to display additional message
      })
  }

  const handleDelete = () => {
    deleteMutation.mutate()
  }

  const footer = existingConfig
    ? [
        <Popconfirm
          key="delete"
          title="Remove SSO configuration?"
          description="This will remove the SSO configuration from this hospital. Users will no longer be able to authenticate via SSO."
          onConfirm={handleDelete}
          okText="Yes, remove"
          cancelText="Cancel"
          okButtonProps={{ danger: true, loading: deleteMutation.isPending }}
        >
          <span style={{ float: 'left' }}>
            <a style={{ color: '#ff4d4f' }}>Remove Configuration</a>
          </span>
        </Popconfirm>,
        <span key="buttons">
          <Space>
            <a onClick={onCancel}>Cancel</a>
            <button
              type="button"
              className="ant-btn ant-btn-primary"
              onClick={handleOk}
              disabled={updateMutation.isPending}
            >
              {updateMutation.isPending ? 'Saving...' : 'Save'}
            </button>
          </Space>
        </span>,
      ]
    : undefined

  return (
    <Modal
      title={existingConfig ? 'Edit SSO Configuration' : 'Configure SSO'}
      open={open}
      onOk={handleOk}
      onCancel={onCancel}
      confirmLoading={updateMutation.isPending}
      width={600}
      footer={footer}
    >
      <Alert
        message="SAML SSO Configuration"
        description="Configure SAML SSO settings for this hospital. Users will be able to authenticate via the specified Auth0 SAML connection."
        type="info"
        showIcon
        style={{ marginBottom: 16 }}
      />

      <Form form={form} layout="vertical">
        <Form.Item
          name="enabled"
          label="SSO Enabled"
          valuePropName="checked"
          tooltip="Enable or disable SSO authentication for this hospital"
        >
          <Switch checkedChildren="Enabled" unCheckedChildren="Disabled" />
        </Form.Item>

        <Form.Item
          name="connection"
          label="Auth0 SAML Connection"
          rules={[
            {
              required: ssoEnabled,
              message: 'Please select a SAML connection when SSO is enabled',
            },
          ]}
          tooltip="The Auth0 SAML connection to use for SSO authentication"
        >
          <Select
            placeholder="Select SAML connection"
            loading={loadingConnections}
            showSearch
            optionFilterProp="children"
            disabled={!ssoEnabled}
          >
            {connectionsData?.map((conn) => (
              <Select.Option key={conn.id} value={conn.name}>
                {conn.displayName || conn.name}
              </Select.Option>
            ))}
          </Select>
        </Form.Item>

        <Form.Item
          name="group"
          label="SAML Group (Optional)"
          tooltip="Optional SAML group claim for additional access control"
        >
          <Input placeholder="e.g. App_XFERALL_SocialWorker" disabled={!ssoEnabled} />
        </Form.Item>

        <Form.Item label="Default Permissions" tooltip="Default permissions granted to SSO users">
          <Space direction="vertical" style={{ width: '100%' }}>
            <Form.Item name={['permissions', 'bi']} valuePropName="checked" noStyle>
              <Checkbox disabled={!ssoEnabled}>BI Access</Checkbox>
            </Form.Item>
            <Form.Item name={['permissions', 'sender']} valuePropName="checked" noStyle>
              <Checkbox disabled={!ssoEnabled}>Sender</Checkbox>
            </Form.Item>
            <Form.Item name={['permissions', 'readonly']} valuePropName="checked" noStyle>
              <Checkbox disabled={!ssoEnabled}>Read Only</Checkbox>
            </Form.Item>
            <Form.Item name={['permissions', 'receiver']} valuePropName="checked" noStyle>
              <Checkbox disabled={!ssoEnabled}>Receiver</Checkbox>
            </Form.Item>
            <Form.Item name={['permissions', 'dashboard']} valuePropName="checked" noStyle>
              <Checkbox disabled={!ssoEnabled}>Dashboard</Checkbox>
            </Form.Item>
            <Form.Item name={['permissions', 'teamInsights']} valuePropName="checked" noStyle>
              <Checkbox disabled={!ssoEnabled}>Team Insights</Checkbox>
            </Form.Item>
          </Space>
        </Form.Item>
      </Form>
    </Modal>
  )
}
